import { type InputRef, Table, Tag } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { isNil, pick, sortBy } from 'lodash/fp';
import { type Key, useCallback, useMemo, useRef } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type TradeInListDataFragment } from '../../../api/fragments/TradeInListData';
import { useGetTradeInListFiltersQuery } from '../../../api/queries/getTradeInListFilters';
import { useListTradeInsQuery } from '../../../api/queries/listTreadeIns';
import { TradeInSortingField, TradeInStatus } from '../../../api/types';
import { useCompany, useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import RangeDateFilterBox, { objectToRangeDateValue } from '../../../components/RangeDateFilterBox';
import SearchBox from '../../../components/SearchBox';
import { useRuntimeConfig } from '../../../runtimeConfig';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useFormatDate from '../../../utilities/useFormatDate';
import useFormats from '../../../utilities/useFormats';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import PriceFeatureLabel from '../../shared/PriceFeatureLabel';
import { type PaginatedApplicationTable } from '../MobilityBookingListPage/MobilityBookingList';
import useListReducer from './useListReducer';
import useSortOrder from './useSortOrder';

export const getSortField = makeGetSortingRule((field): TradeInSortingField => {
    switch (field) {
        case 'versioning.createdAt':
            return TradeInSortingField.ApplicationDate;

        case 'identifier':
            return TradeInSortingField.Identifier;

        case 'licensePlateNumber':
            return TradeInSortingField.VehicleNo;

        case 'vehicleMake':
            return TradeInSortingField.VehicleMake;

        case 'vehicleModel':
            return TradeInSortingField.VehicleModel;

        case 'manufacturingYear':
            return TradeInSortingField.ManufacturingYear;

        case 'selectedDealer.dealerName':
            return TradeInSortingField.SelectedDealer;

        case 'selectedDealer.offerValue':
            return TradeInSortingField.OfferPrice;

        case 'status':
            return TradeInSortingField.Status;

        default:
            throw new Error('Such Field is not supported');
    }
});

export const getStatusColor = (status: string): string => {
    switch (status) {
        case TradeInStatus.Rejected:
            return 'red';

        case TradeInStatus.Accepted:
            return 'green';

        case TradeInStatus.BidClosed:
            return 'purple';

        case TradeInStatus.Drafted:
            return 'grey';

        default:
            return '';
    }
};

export const renderStatusTag = (value: string, t: TFunction) => {
    const color = getStatusColor(value);

    return (
        <Tag key={value} color={color}>
            {t(`tradeInList:status.${value}`)}
        </Tag>
    );
};

type TradeInDataSource = TradeInListDataFragment & { key: Key };

type PaginatedTradeInTable = PaginatedTableProps<TradeInDataSource>;

export type TradeInListProps = {
    moduleIds: string[];
};

const TradeInList = ({ moduleIds }: TradeInListProps) => {
    const { t } = useTranslation(['tradeInList', 'common']);

    const company = useCompany(true);
    const formatDate = useFormatDate();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.TradeIn);
    const [state, dispatch] = useListReducer(currentCache);
    const { page, pageSize, sort, filter } = state;

    const navigate = useNavigate();
    const inputRef = useRef<InputRef>(null);

    const { data } = useListTradeInsQuery({
        nextFetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: {
                ...filter,
                moduleIds,
            },
        },
    });

    const dataSource = data?.list?.items;
    const total = data?.list?.count || 0;

    const { data: filterList } = useGetTradeInListFiltersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { moduleIds },
        },
    });

    const statusList = useMemo(() => {
        const filters = filterList?.getTradeInListFilters;
        if (!filterList) {
            return [];
        }

        return sortBy(
            'text',
            filters.statuses.map(status => ({
                text: t(`tradeInList:status.${status}`),
                value: status,
            }))
        );
    }, [filterList]);

    const { formatAmountWithCurrency } = useFormats(
        company?.currency || '',
        company?.roundings?.amount?.decimals || 0,
        company?.roundings?.percentage?.decimals || 0
    );

    const {
        appDateSort,
        identifierSort,
        vehicleNoSort,
        vehicleMakeSort,
        vehicleModelSort,
        manufacturingYearSort,
        selectedDealerSort,
        offerPriceSort,
        statusSort,
    } = useSortOrder(sort);

    const showSearchDropDown = useCallback(
        (props: FilterDropdownProps, dataIndex: string) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const showRangeFilterDropDown = useCallback(
        (props: FilterDropdownProps) =>
            RangeDateFilterBox({
                filterDropDownProps: props,
                picker: 'date',
            }),
        []
    );

    const onChange = useCallback<PaginatedApplicationTable['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort': {
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                }

                case 'filter': {
                    return dispatch({
                        type: 'setFilter',
                        filteringRule: {
                            identifier: !filters.identifier ? undefined : (filters.identifier[0] as string),
                            vehicleNo: !filters.vehicleNo ? undefined : (filters.vehicleNo[0] as string),
                            vehicleMake: !filters.vehicleMake ? undefined : (filters.vehicleMake[0] as string),
                            vehicleModel: !filters.vehicleModel ? undefined : (filters.vehicleModel[0] as string),
                            manufacturingYear: !filters.manufacturingYear
                                ? undefined
                                : (filters.manufacturingYear[0] as string),
                            selectedDealer: !filters.selectedDealer ? undefined : (filters.selectedDealer[0] as string),
                            status: !filters.status ? undefined : (filters.status as TradeInStatus[]),
                            createdDateRange: !filters.appDate
                                ? undefined
                                : {
                                      start: new Date(filters.appDate[0] as string),
                                      end: new Date(filters.appDate[1] as string),
                                  },
                        },
                    });
                }

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const onRow = useCallback<PaginatedTradeInTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    const { timeZonesByModuleId } = useCompanyContext();
    const { instance } = useRuntimeConfig();

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('tradeInList:title')}
            total={total}
        >
            <Table.Column
                key="appDate"
                dataIndex={['versioning', 'createdAt']}
                filterDropdown={props => showRangeFilterDropDown(props)}
                filteredValue={objectToRangeDateValue(filter.createdDateRange)}
                render={(value, record: TradeInListDataFragment) =>
                    !isNil(value)
                        ? formatDate({
                              date: value,
                              timeZone: timeZonesByModuleId[record.moduleId],
                              withOffset: true,
                          })
                        : ''
                }
                sortOrder={appDateSort}
                title={t('tradeInList:columns.applicationDate')}
                sorter
            />

            <Table.Column
                key="identifier"
                dataIndex={['identifier']}
                filterDropdown={props => showSearchDropDown(props, 'identifier')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.identifier ? [filter.identifier] : undefined}
                sortOrder={identifierSort}
                title={t('tradeInList:columns.identifier')}
                sorter
            />
            <Table.Column
                key="vehicleNo"
                dataIndex={['licensePlateNumber']}
                filterDropdown={props => showSearchDropDown(props, 'vehicleNo')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.vehicleNo ? [filter.vehicleNo] : undefined}
                sortOrder={vehicleNoSort}
                title={t('tradeInList:columns.vehicleNo')}
                sorter
            />
            <Table.Column
                key="vehicleMake"
                dataIndex={['vehicleMake']}
                filterDropdown={props => showSearchDropDown(props, 'vehicleMake')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.vehicleMake ? [filter.vehicleMake] : undefined}
                sortOrder={vehicleMakeSort}
                title={t('tradeInList:columns.vehicleMake')}
                sorter
            />
            <Table.Column
                key="vehicleModel"
                dataIndex={['vehicleModel']}
                filterDropdown={props => showSearchDropDown(props, 'vehicleModel')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.vehicleModel ? [filter.vehicleModel] : undefined}
                sortOrder={vehicleModelSort}
                title={t('tradeInList:columns.vehicleModel')}
                sorter
            />
            <Table.Column
                key="manufacturingYear"
                dataIndex={['manufacturingYear']}
                filterDropdown={props => showSearchDropDown(props, 'manufacturingYear')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.manufacturingYear ? [filter.manufacturingYear] : undefined}
                sortOrder={manufacturingYearSort}
                title={t('tradeInList:columns.manufacturingYear')}
                sorter
            />
            <Table.Column
                key="selectedDealer"
                dataIndex={['selectedDealer', 'dealerName']}
                filterDropdown={props => showSearchDropDown(props, 'selectedDealer')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.selectedDealer ? [filter.selectedDealer] : undefined}
                sortOrder={selectedDealerSort}
                title={t('tradeInList:columns.awardedDealer')}
                sorter
            />
            {instance !== 'audi' && (
                <Table.Column
                    key="offerPrice"
                    dataIndex={['selectedDealer', 'offerValue']}
                    render={(value: number, record: TradeInListDataFragment) => {
                        if (record.module.__typename !== 'TradeInModule') {
                            return value;
                        }

                        return (
                            <PriceFeatureLabel
                                currency={record.module.company.currency}
                                decimals={record.module.company.roundings.amount.decimals}
                                percentages={record.module.company.roundings.percentage.decimals}
                                value={value}
                            />
                        );
                    }}
                    sortOrder={offerPriceSort}
                    title={t('tradeInList:columns.offerPrice')}
                    sorter
                />
            )}
            <Table.Column
                key="status"
                dataIndex={['status']}
                filterDropdown={props => showFilterDropDown(props, statusList)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.status}
                onFilter={(value: string, record: TradeInListDataFragment) => record.status.indexOf(value) === 0}
                render={value => value && renderStatusTag(value, t)}
                sortOrder={statusSort}
                title={t('tradeInList:columns.status')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default TradeInList;
